import { useEffect, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { apiUrl } from "../../utils/api";
import type { RiskSummary } from "./types";
import {
  RisikoanschriftCard,
  VersicherteSacheBlock,
  GewerbeTypBlock,
  BaujahrBlock,
} from "./AllgemeinBlocks";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  block: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    paddingTop: "12px",
    paddingBottom: "12px",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  blockFirst: {
    ...shorthands.borderTop("0"),
    paddingTop: "0",
  },
  blockTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
    margin: 0,
  },
  details: {
    backgroundColor: BRAND.white,
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    overflow: "hidden",
  },
  summary: {
    cursor: "pointer",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    fontWeight: 700,
    color: BRAND.ink,
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    listStyle: "none",
    "::-webkit-details-marker": { display: "none" },
  },
  summaryHint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
    fontWeight: 400,
  },
  summaryCaret: {
    width: "18px",
    height: "18px",
    color: BRAND.purple,
    flexShrink: 0,
    transitionProperty: "transform",
    transitionDuration: "180ms",
  },
  detailsContent: {
    paddingTop: "12px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  loading: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    color: BRAND.muted,
  },
});

const fetchRiskSummary = async (id: number): Promise<RiskSummary | null> => {
  try {
    const res = await fetch(apiUrl(`risk-objects/${id}`));
    if (!res.ok) return null;
    const data = await res.json();
    const charsArr: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }> =
      Array.isArray(data.characteristics) ? data.characteristics : [];
    const characteristics: Record<string, string> = {};
    for (const c of charsArr) {
      if (c.nameBezeichnung) characteristics[c.nameBezeichnung] = String(c.riskCharacteristicValue ?? "");
    }
    return {
      id: data.id,
      name: data.name ?? "",
      street: characteristics.street ?? "",
      postalCode: characteristics.postalCode ?? "",
      city: characteristics.city ?? "",
      country: characteristics.country ?? "Deutschland",
      skizzeSvg: data.skizzeSvg ?? null,
      characteristics,
      eigentuemerArt:
        characteristics.eigentuemerArt ?? characteristics.eigentumerArt ?? characteristics.eigentuemer ?? undefined,
      contacts: [],
    };
  } catch (e) {
    console.error(e);
    return null;
  }
};

const RiskBody = ({
  risk,
  baujahrValue,
  onBaujahrChange,
  geocode = false,
}: {
  risk: RiskSummary;
  baujahrValue?: string;
  onBaujahrChange?: (v: string) => void;
  /** Nur sichtbare Risiken geokodieren — siehe Gate unten. */
  geocode?: boolean;
}) => {
  return (
    <>
      <RisikoanschriftCard risk={risk} geocode={geocode} />
      <VersicherteSacheBlock risk={risk} />
      {(risk.characteristics.kategorie ?? "").toLowerCase().includes("gewerbe") && <GewerbeTypBlock risk={risk} />}
      <BaujahrBlock
        risk={risk}
        value={baujahrValue ?? risk.characteristics.baujahr ?? ""}
        onChange={onBaujahrChange ?? (() => {})}
      />
    </>
  );
};

const RisikoinfoSection = ({
  hauptrisiko,
  temporaereRisikoIds,
  baujahr,
  onBaujahrChange,
}: {
  hauptrisiko: RiskSummary | null;
  temporaereRisikoIds: number[];
  baujahr?: string;
  onBaujahrChange?: (v: string) => void;
}) => {
  const styles = useStyles();
  const [tempRisks, setTempRisks] = useState<Record<number, RiskSummary | null>>({});
  // Gate gegen 1+N Geocode-Anfragen: die <details> der temporär vermieteten
  // Risiken sind eager gemountet, also wird erst beim Aufklappen geokodiert.
  const [offeneRisiken, setOffeneRisiken] = useState<Record<number, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next: Record<number, RiskSummary | null> = {};
      await Promise.all(
        temporaereRisikoIds.map(async (id) => {
          next[id] = await fetchRiskSummary(id);
        }),
      );
      if (!cancelled) setTempRisks(next);
    };
    // Aufklapp-Zustände verwaister IDs mitentsorgen, damit der State nicht
    // mitwächst, wenn sich die Risikoliste ändert.
    setOffeneRisiken((vorher) => {
      const gefiltert: Record<number, boolean> = {};
      for (const id of temporaereRisikoIds) if (vorher[id]) gefiltert[id] = true;
      // Referenz nur wechseln, wenn wirklich etwas wegfällt — sonst gäbe es bei
      // jeder Listenänderung ein überflüssiges Re-Render.
      return Object.keys(gefiltert).length === Object.keys(vorher).length ? vorher : gefiltert;
    });
    if (temporaereRisikoIds.length) load();
    else setTempRisks({});
    return () => {
      cancelled = true;
    };
  }, [temporaereRisikoIds]);

  if (!hauptrisiko) {
    return (
      <div className={styles.loading}>
        Hauptrisiko noch nicht ausgewählt — bitte den vorherigen Schritt abschließen.
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={mergeClasses(styles.block, styles.blockFirst)}>
        <h3 className={styles.blockTitle}>Hauptrisiko</h3>
        <RiskBody risk={hauptrisiko} baujahrValue={baujahr} onBaujahrChange={onBaujahrChange} geocode />
      </div>

      {temporaereRisikoIds.length > 0 && (
        <div className={styles.block}>
          <h3 className={styles.blockTitle}>Temporär vermietete Risiken</h3>
          {temporaereRisikoIds.map((id) => {
            const r = tempRisks[id];
            return (
              <details
                key={id}
                className={styles.details}
                onToggle={(e) => {
                  // Den Wert VOR dem State-Updater festhalten: React setzt nach
                  // dem Dispatch `event.currentTarget = null`, und der Updater
                  // läuft nur bei der Eager-State-Optimierung synchron. Steht
                  // ein anderes Update an (z. B. das setTempRisks aus dem
                  // Fetch-Effekt), liefe er erst in der Render-Phase — dort
                  // wäre `currentTarget` bereits null.
                  const istOffen = e.currentTarget.open;
                  setOffeneRisiken((vorher) => ({ ...vorher, [id]: istOffen }));
                }}
              >
                <summary className={styles.summary}>
                  <svg className={styles.summaryCaret} viewBox="0 0 16 16" fill="none">
                    <path d="M5 4l5 4-5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {r ? r.name || `${r.street}, ${r.city}` : `Risiko ${id}`}
                  <span className={styles.summaryHint}>Risiko-ID {id}</span>
                </summary>
                <div className={styles.detailsContent}>
                  {r ? (
                    <RiskBody risk={r} geocode={!!offeneRisiken[id]} />
                  ) : (
                    <div className={styles.loading}>Lade Risiko-Details…</div>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RisikoinfoSection;
